import type { PropsWithChildren } from 'react';
import { Children as ReactChildren, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Slider } from '@elfsight/lib-toolkit-slider';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../../meta';
import { Icon } from './Icon';

const classNamePrefix = `eapp-${meta.alias}`;

interface CarouselProps extends PropsWithChildren {
  viewWidth: number;
  viewHeight: number;
  compact: boolean;
  controlsColor?: string;
  slideIndex?: number;
}

export function Carousel({
  viewHeight,
  compact,
  controlsColor,
  slideIndex,
  children
}: CarouselProps) {
  const { displayDeactivation } = useSettings();
  const sliderRef = useRef();

  useEffect(() => {
    const { current: slider } = sliderRef;

    if (slider && slideIndex !== undefined && slideIndex !== null) {
      slider.goTo(slideIndex);
    }
  }, [slideIndex, sliderRef]);

  const sliderIconArrowPrev = (
    <Icon type="arrow-tailed-medium" color={controlsColor} rotation={180} />
  );

  const sliderIconArrowNext = (
    <Icon type="arrow-tailed-medium" color={controlsColor} />
  );

  return (
    <Container
      _viewHeight={viewHeight}
      _compact={compact}
      _paginationColor={controlsColor}
      _displayDeactivation={displayDeactivation}
    >
      <Slider
        ref={sliderRef}
        appClassnamePrefix="eapp-before-and-after-slider"
        slidesPerView={1}
        animationType="fade"
        paginationClassName={classNamePrefix}
        itemClassName={classNamePrefix}
        navigationClassName={classNamePrefix}
        drag={false}
        mobileDrag={false}
        paginationType="numbers"
        customIcons={{
          prev: sliderIconArrowPrev,
          next: sliderIconArrowNext
        }}
        colors={{
          arrows: {
            background: 'transparent',
            hover: {
              background: 'transparent'
            }
          },
          pagination: {
            color: 'transparent'
          }
        }}
      >
        {ReactChildren.count(children) > 0 &&
          ReactChildren.map(children, (child, index) => (
            <Item key={child.key || index} _viewHeight={viewHeight}>
              {child}
            </Item>
          ))}
      </Slider>
    </Container>
  );
}

const Container = styled.div<{
  _viewHeight: number;
  _compact: boolean;
  _displayDeactivation?: boolean;
  _paginationColor?: string;
}>`
  /*
    сейчас будут ахуенные костыли,
    чтобы сделать это без угрызений совести,
    мне пришлось выпить виски

    ¯\_(ツ)_/¯
  */

  &
    div.${classNamePrefix}-arrow,
    &
    div.${classNamePrefix}-arrow:hover,
    &
    div.${classNamePrefix}-arrow:active {
    outline: none;

    ${({ _displayDeactivation }) =>
      _displayDeactivation &&
      css`
        pointer-events: none;
      `};

    ${({ _viewHeight }) => css`
      top: ${_viewHeight + 4}px;
    `};
  }

  & div.${classNamePrefix}-arrow-previous {
    left: auto;

    ${({ _compact }) => css`
      right: ${_compact ? 36 : 40}px;
    `};
  }

  & div.${classNamePrefix}-arrow-next {
    ${({ _compact }) => css`
      right: ${_compact ? 8 : 12}px;
    `};
  }

  & div.${classNamePrefix}-pagination-component {
    bottom: auto;
    width: auto;
    min-width: 0;

    ${({ _viewHeight, _compact }) => css`
      top: ${_viewHeight + 10}px;
      left: ${_compact ? 8 : 12}px;
    `};
  }

  & div.${classNamePrefix}-numbers-component {
    width: initial;
    height: auto;
    margin: 0;
  }

  & div.${classNamePrefix}-numbers-number {
    font-size: 16px;
    line-height: 1.25;

    ${({ _paginationColor }) => css({ color: _paginationColor })};
  }
`;

const Item = styled.div<{
  _viewHeight: number;
}>`
  ${({ _viewHeight }) => css`
    min-height: ${_viewHeight + 32}px;
  `};
`;
